import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { MapContainer, TileLayer, ScaleControl, ImageOverlay, Marker, Tooltip, GeoJSON } from "react-leaflet";
import type { LatLngBoundsExpression, Layer, LeafletMouseEvent, StyleFunction, GeoJSON as LeafletGeoJSON } from "leaflet";
import type { Feature, Geometry, FeatureCollection } from "geojson";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import TeamHeader from "../TeamHeader/TeamHeader";
import "./Environmental.css";
import countiesGeoJson from '../GisTab/counties_southeast.json';

// Assets
import tempPic from './assets/temperature-card.png'
import humidityPic from './assets/humidity-card.png'
import windPic from './assets/wind-speed-card.png'
import preassurePic from './assets/preassure-card.png'
import solarPic from './assets/solar-radiation-card.png'
import mrmsPic from './assets/mrms-card.png'

type VariableKey = "temp" | "humid" | "wspd" | "slp" | "sol" | "precip1" | "precip2";

interface VariableConfig {
    key: VariableKey;
    label: string;
    shortDescription: string;
    aboutTitle: string;
    aboutDescription: string;
    image: string;
}

const LEGEND_BAR_HEIGHT = 180; // px — must match .env-legend-row height in CSS
const MIN_LABEL_GAP = 14;      // px — minimum vertical space between label centers

// Given top-position percentages (any order, 0 = top of bar, 100 = bottom),
// push labels apart just enough to avoid overlapping text, then return the
// adjusted positions in the SAME order as the input array.
const declutterTopPositions = (topPercents: number[]): number[] => {
    const raw = topPercents.map(p => (p / 100) * LEGEND_BAR_HEIGHT);

    // Work on a copy sorted by actual position, remembering original indices
    const order = raw.map((_, i) => i).sort((a, b) => raw[a] - raw[b]);
    const sortedPx = order.map(i => raw[i]);

    for (let i = 1; i < sortedPx.length; i++) {
        if (sortedPx[i] - sortedPx[i - 1] < MIN_LABEL_GAP) {
            sortedPx[i] = sortedPx[i - 1] + MIN_LABEL_GAP;
        }
    }
    const overflow = sortedPx[sortedPx.length - 1] - LEGEND_BAR_HEIGHT;
    if (overflow > 0) {
        for (let i = 0; i < sortedPx.length; i++) sortedPx[i] -= overflow;
        for (let i = 1; i < sortedPx.length; i++) {
            if (sortedPx[i] - sortedPx[i - 1] < MIN_LABEL_GAP) {
                sortedPx[i] = sortedPx[i - 1] + MIN_LABEL_GAP;
            }
        }
    }
    const clamped = sortedPx.map(v => Math.max(0, Math.min(LEGEND_BAR_HEIGHT, v)));

    // map back to the caller's original order
    const result = new Array(topPercents.length);
    order.forEach((originalIdx, sortedIdx) => {
        result[originalIdx] = clamped[sortedIdx];
    });
    return result;
};

// currentData.timestamp is a naive "YYYY-MM-DD HH:mm:ss" string that the backend has
// already converted from UTC into America/New_York wall-clock time. We parse it
// manually (rather than `new Date(rawString)`) because browsers are inconsistent
// about whether a timezone-less string like this is treated as local time or UTC.
const formatEasternTimestamp = (raw: string): string => {
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (!match) return `${raw} ET`;

    const [, year, month, day, hour, minute] = match;
    // Constructing with explicit numeric args treats them as local components —
    // we're just using Date as a formatting helper here, not converting timezones.
    const dt = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));

    const datePart = dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const timePart = dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

    return `${datePart}, ${timePart} ET`;
};

const VARIABLES: VariableConfig[] = [
    {
        key: "temp",
        label: "24-hour Temperature Variation",
        shortDescription: "Current air temperature across Florida",
        aboutTitle: "About the Air Temperature Map",
        aboutDescription:
            "The Air Temperature map plots the air temperature (°F) at the standard height of 1.5 m (5 feet) across Florida. Warmer areas are shaded in red and orange tones, while cooler areas appear in blue. Use the hour slider to step through the day and see how temperatures shift from morning through evening.",
        image: tempPic,
    },
    {
        key: "humid",
        label: "24-hour Relative Humidity Variation",
        shortDescription: "Relative humidity levels statewide",
        aboutTitle: "About the Humidity Map",
        aboutDescription:
            "The Humidity map plots relative humidity (%) across Florida, showing how much moisture is in the air relative to what it can hold at the current temperature. Darker blue areas indicate higher humidity, which is common in Florida due to its coastal, subtropical climate. This is useful for tracking muggy conditions, fog potential, and comfort levels throughout the day.",
        image: humidityPic,
    },
    {
        key: "wspd",
        label: "24-hour Wind Speed and Direction",
        shortDescription: "Surface wind speed and direction",
        aboutTitle: "About the Wind Speed Map",
        aboutDescription:
            "The Wind Speed map plots surface wind speed (mph) across Florida. Brighter yellow-green areas indicate stronger winds, while darker purple areas indicate calmer conditions. Arrows show wind direction, pointing the way the wind is blowing. This map is helpful for tracking sea breeze fronts, approaching weather systems, and general wind patterns across the state throughout the day.",
        image: windPic,
    },
    {
        key: "slp",
        label: "24-hour Sea Level Pressure Variation",
        shortDescription: "Sea level pressure across Florida",
        aboutTitle: "About the Sea Level Pressure Map",
        aboutDescription:
            "The Sea Level Pressure map plots barometric pressure (mb), interpolated from ASOS station observations, adjusted to sea level. Pressure gradients drive wind and can signal approaching fronts or storm systems — tighter gradients (rapid color transitions) generally indicate stronger winds, while broad, flat areas indicate calmer, more stable conditions.",
        image: preassurePic,
    },
    {
        key: "sol",
        label: "24-hour Solar Radiation Variation",
        shortDescription: "Incoming solar radiation statewide",
        aboutTitle: "About the Solar Radiation Map",
        aboutDescription:
            "The Solar Radiation map plots incoming shortwave solar radiation (W/m²) across Florida, interpolated from FAWN and ASOS station observations. Brighter areas indicate stronger sunlight, useful for tracking cloud cover, fire weather potential, and diurnal heating patterns throughout the day.",
        image: solarPic,
    },
    {
        key: "precip1",
        label: "24-hour Precipitation (Pass 1)",
        shortDescription: "MRMS multi-sensor precipitation, first pass",
        aboutTitle: "About the Precipitation (Pass 1) Map",
        aboutDescription:
            "The Precipitation Pass 1 map plots hourly rainfall rate (mm/hr) from NOAA's Multi-Sensor QPE product. This is the initial, near-real-time precipitation estimate for each hour, combining radar and gauge data as soon as it becomes available.",
        image: mrmsPic,
    },
    {
        key: "precip2",
        label: "24-hour Precipitation (Pass 2)",
        shortDescription: "MRMS multi-sensor precipitation, refined pass",
        aboutTitle: "About the Precipitation (Pass 2) Map",
        aboutDescription:
            "The Precipitation Pass 2 map plots a refined hourly rainfall rate (mm/hr) from NOAA's Multi-Sensor QPE product, incorporating additional gauge data that arrives after the initial pass. Because this refinement lags by about an hour, the most recent hour may not yet have a Pass 2 value available.",
        image: mrmsPic,
    },
];

interface LegendBin {
    from: number;
    to: number;
    color: string;
}

interface WindVector {
    lat: number;
    lon: number;
    direction: number; // degrees, meteorological "from" convention
    speed: number | null;
}

interface VariableDataResponse {
    variable: string;
    time_index: number;
    timestamp: string;
    unit: string;
    min: number | null;
    max: number | null;
    legend_levels: number[];
    legend_bins: LegendBin[];
    bounds: LatLngBoundsExpression;
    county_values: CountyValue[];
    image: string;
}

interface WindDataResponse {
    time_index: number;
    timestamp: string;
    unit: string;
    vectors: WindVector[];
}

interface CountyValue {
    name: string;
    lat: number;
    lon: number;
    value: number;
}

const DEFAULT_CENTER: [number, number] = [27.8, -82.5];

// Rotated arrow icon for wind direction. `direction` is meteorological
// degrees (where the wind is coming FROM). We rotate by direction + 180
// so the arrow points the way the wind is blowing TO. If your arrows look
// backwards on the map, drop the "+ 180".
// Wind hours can carry tens of thousands of vectors. Building a brand-new
// L.divIcon (parsing a fresh inline SVG string) for every marker on every
// render was the actual source of the frontend freezing/crashing on dense
// hours -- not the marker count by itself. Cache icons by rotation bucket
// (rounded to the nearest 5°) so we create at most 72 icon instances total,
// reused across all markers and all renders, instead of one per marker.
const windIconCache = new Map<number, L.DivIcon>();
const windArrowIcon = (direction: number): L.DivIcon => {
    const bucket = Math.round(direction / 5) * 5 % 360;
    const cached = windIconCache.get(bucket);
    if (cached) return cached;

    const rotation = (bucket + 180) % 360;
    const icon = L.divIcon({
        className: "wind-arrow-icon",
        html: `<svg width="16" height="16" viewBox="0 0 24 24" style="transform: rotate(${rotation}deg);">
                 <path d="M12 3 L12 21 M12 3 L6 10 M12 3 L18 10" stroke="#1f2937" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
               </svg>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
    });
    windIconCache.set(bucket, icon);
    return icon;
};

const countyValueIcon = (label: string) => {
    return L.divIcon({
        className: "county-value-icon",
        html: `<div style="
            transform: translate(-50%, -50%);
            display: flex;
            align-items: center;
            justify-content: center;
            box-sizing: border-box;
            min-width: 26px;
            height: 20px;
            padding: 2px 6px;
            background: rgba(17, 24, 39, 0.85);
            color: #fff;
            font-size: 11px;
            font-weight: 600;
            line-height: 1;
            border-radius: 4px;
            border: 1px solid rgba(255,255,255,0.6);
            white-space: nowrap;
        ">${label}</div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
    });
};

interface CountyFeatureProps {
    NAME: string;
    STATEFP: string;
    STATE?: string;
    [key: string]: any;
}

const CountyBordersLayer: React.FC = () => {
    const geoJsonRef = useRef<LeafletGeoJSON | null>(null);
    const [geoData, setGeoData] = useState<FeatureCollection | null>(null);

    useEffect(() => {
        setGeoData(countiesGeoJson as FeatureCollection);
    }, []);

    // Florida only — filter out AL, GA, SC, TX counties from the shared file
    const floridaGeoData = useMemo<FeatureCollection | null>(() => {
        if (!geoData) return null;
        return {
            ...geoData,
            features: geoData.features.filter((feature) => {
                const props = feature.properties as CountyFeatureProps;
                const state = props?.STATE ?? "";
                // STATEFP "12" is the FIPS code for Florida; also check STATE name as a fallback
                return props?.STATEFP === "12" || state === "Florida" || state === "FL";
            }),
        };
    }, [geoData]);

    // No fill — just an outline
    const styleFn: StyleFunction<CountyFeatureProps> = useCallback(() => ({
        fillOpacity: 0,
        color: "#374151",
        weight: 1,
        interactive: true,
    }), []);

    const onEachFeature = useCallback((feature: Feature<Geometry, CountyFeatureProps>, layer: Layer) => {
        const name = feature.properties?.NAME ?? "";

        layer.bindTooltip(
            `${name} County`,
            {
                className: "county-tooltip",
                sticky: false,
                direction: "top",
            }
        );

        layer.on({
            mouseover: (e: LeafletMouseEvent) => {
                const target = e.target;
                target.setStyle({ weight: 2.5, color: "#111827" });
                target.bringToFront();
                target.openTooltip();
            },
            mouseout: (e: LeafletMouseEvent) => {
                const target = e.target;
                geoJsonRef.current?.resetStyle(target);
                target.closeTooltip();
            },
        });
    }, []);

    if (!floridaGeoData) return null;

    return (
        <GeoJSON
            ref={geoJsonRef}
            data={floridaGeoData}
            style={styleFn as any}
            onEachFeature={onEachFeature as any}
        />
    );
};

const Environmental = () => {
    const [loadingMeta, setLoadingMeta] = useState(true);
    const [timeSteps, setTimeSteps] = useState(24);
    const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER);

    const [activeTab, setActiveTab] = useState<VariableKey | null>(null);
    const [timeIndex, setTimeIndex] = useState(0);

    const [loadingData, setLoadingData] = useState(false);
    const [currentData, setCurrentData] = useState<VariableDataResponse | null>(null);
    const [windData, setWindData] = useState<WindDataResponse | null>(null);

    const [showWindArrows, setShowWindArrows] = useState(true);
    const [showCounties, setShowCounties] = useState(true);
    const [showCountyValues, setShowCountyValues] = useState(true);

    const cache = useRef<Map<string, VariableDataResponse>>(new Map());
    const windCache = useRef<Map<number, WindDataResponse>>(new Map());

    useEffect(() => {
        const loadMeta = async () => {
            try {
                const response = await fetch("/api/environmental/", {
                    credentials: "same-origin",
                });
                const data = await response.json();

                setTimeSteps(data.dimensions.time ?? 24);

                const lat = data.coordinates.find((c: any) => c.name === "lat");
                const lon = data.coordinates.find((c: any) => c.name === "lon");
                if (lat?.min != null && lat?.max != null && lon?.min != null && lon?.max != null) {
                    setMapCenter([(lat.min + lat.max) / 2, (lon.min + lon.max) / 2]);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingMeta(false);
            }
        };

        loadMeta();
    }, []);

    const loadVariableData = useCallback(async (variable: VariableKey, index: number) => {
        const cacheKey = `${variable}-${index}`;
        const cached = cache.current.get(cacheKey);
        if (cached) {
            setCurrentData(cached);
            return;
        }

        setLoadingData(true);
        setCurrentData(null); // clear any stale layer while we fetch the new one
        try {
            const response = await fetch(
                `/api/environmental/data/?variable=${variable}&time_index=${index}`,
                { credentials: "same-origin" }
            );
            const data = await response.json();

            // The backend returns e.g. {"error": "time_index out of range"} with a
            // non-2xx status when the upstream frame list for this variable/hour is
            // empty (this happens for precip1/precip2 when MRMS hasn't produced a
            // frame yet). Guard against that — and against any payload missing the
            // fields ImageOverlay needs — instead of trusting the JSON shape blindly.
            if (!response.ok || !data || !data.image || !data.bounds) {
                setCurrentData(null);
                return;
            }

            const typed = data as VariableDataResponse;
            cache.current.set(cacheKey, typed);
            setCurrentData(typed);
        } catch (err) {
            console.error(err);
            setCurrentData(null);
        } finally {
            setLoadingData(false);
        }
    }, []);

    const loadWindData = useCallback(async (index: number) => {
        const cached = windCache.current.get(index);
        if (cached) {
            setWindData(cached);
            return;
        }
        try {
            const response = await fetch(
                `/api/environmental/wind/?time_index=${index}&stride=4`,
                { credentials: "same-origin" }
            );
            const data = await response.json();

            if (!response.ok || !data || !Array.isArray(data.vectors)) {
                setWindData(null);
                return;
            }

            const typed = data as WindDataResponse;
            windCache.current.set(index, typed);
            setWindData(typed);
        } catch (err) {
            console.error(err);
            setWindData(null);
        }
    }, []);

    useEffect(() => {
        if (activeTab) {
            loadVariableData(activeTab, timeIndex);
        }
        if (activeTab === "wspd") {
            loadWindData(timeIndex);
        } else {
            setWindData(null);
        }
    }, [activeTab, timeIndex, loadVariableData, loadWindData]);

    const handleSelectVariable = (key: VariableKey) => {
        setCurrentData(null);
        setWindData(null);
        setTimeIndex(0);
        setActiveTab(key);
    };

    const handleBackToMenu = () => {
        setActiveTab(null);
        setCurrentData(null);
        setWindData(null);
    };

    const activeConfig = VARIABLES.find(v => v.key === activeTab) ?? null;

    // Position (0-100%) of a legend level along the min→max gradient bar
    const legendPct = (v: number) => {
        if (!currentData) return 0;

        // NOAA precipitation legend uses fixed boundaries
        if (activeTab === "precip1" || activeTab === "precip2") {
            const max = 250; // last PRECIP_BOUND
            return Math.min(100, Math.max(0, (v / max) * 100));
        }

        if (currentData.min == null || currentData.max == null) return 0;

        const { min, max } = currentData;
        if (max === min) return 0;

        return Math.min(100, Math.max(0, ((v - min) / (max - min)) * 100));
    };

    // Only render ImageOverlay when we actually have a usable image + bounds.
    // Passing url/bounds as undefined is what crashes Leaflet's _initImage
    // (it tries to read .tagName off an image element it never created).
    const hasRenderableLayer = !!(currentData && currentData.image && currentData.bounds);

    return (
        <div className="customer-directory-body">
            <TeamHeader title="Environmental" />

            <div className="support-container">
                {activeConfig ? (
                    <div className="env-detail-header">
                        <button type="button" className="env-back-button" onClick={handleBackToMenu}>
                            &larr; Back to maps
                        </button>
                        <div>
                            <h1 className="support-page-title">{activeConfig.label}</h1>
                            <p className="support-page-sub">{activeConfig.shortDescription}</p>
                        </div>
                    </div>
                ) : (
                    <div className="support-page-header">
                        <div>
                            <h1 className="support-page-title">Florida Environmental Data (v1.0)</h1>
                            <p className="support-page-sub">
                                The FNN Florida Environmental Data Platform v1.0 provides real-time and historical wildfire-weather
                                products for Florida, including hourly 1 km × 1 km gridded fields of key environmental variables derived
                                from meteorological networks and gridded precipitation sources. The platform supports visualization through
                                the internal FNN Portal, enables access to environmental conditions across time and space, and establishes
                                the foundation for future wildfire risk calculations and HRL model improvement.
                            </p>
                        </div>
                    </div>
                )}

                {loadingMeta ? (
                    <div className="tv-loading">Loading dataset...</div>
                ) : activeConfig ? (
                    <div className="env-map-section">
                        <div className="env-controls-row">
                            <div className="env-time-control">
                                <label htmlFor="time-slider">
                                    Hour {timeIndex}
                                    {currentData?.timestamp ? ` — ${formatEasternTimestamp(currentData.timestamp)}` : ""}
                                </label>
                                <input
                                    id="time-slider"
                                    type="range"
                                    min={0}
                                    max={timeSteps - 1}
                                    value={timeIndex}
                                    onChange={e => setTimeIndex(Number(e.target.value))}
                                />
                            </div>

                            <div className="env-layer-toggles">
                                {activeTab === "wspd" && (
                                    <label className="env-toggle">
                                        <span className="env-toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={showWindArrows}
                                                onChange={e => setShowWindArrows(e.target.checked)}
                                            />
                                            <span className="env-toggle-thumb" />
                                        </span>
                                        Wind arrows
                                    </label>
                                )}

                                <label className="env-toggle">
                                    <span className="env-toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={showCounties}
                                            onChange={e => setShowCounties(e.target.checked)}
                                        />
                                        <span className="env-toggle-thumb" />
                                    </span>
                                    Counties
                                </label>

                                <label className="env-toggle">
                                    <span className="env-toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={showCountyValues}
                                            onChange={e => setShowCountyValues(e.target.checked)}
                                        />
                                        <span className="env-toggle-thumb" />
                                    </span>
                                    County values
                                </label>
                            </div>
                        </div>

                        <div className="env-map-wrapper">
                            {loadingData && <div className="env-map-loading">Loading layer...</div>}

                            <MapContainer
                                center={mapCenter}
                                zoom={7}
                                scrollWheelZoom={true}
                                className="env-map"
                            >
                                <TileLayer
                                    attribution='&copy; OpenStreetMap contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <ScaleControl position="bottomleft" />

                                {showCounties && <CountyBordersLayer />}

                                {hasRenderableLayer && (
                                    <ImageOverlay
                                        key={`${currentData!.variable}-${currentData!.time_index}`}
                                        url={currentData!.image}
                                        bounds={currentData!.bounds}
                                        opacity={0.75}
                                    />
                                )}

                                {/* Wind Marker */}
                                {showWindArrows && activeTab === "wspd" && windData?.vectors?.map((v, idx) => (
                                    <Marker
                                        key={`wind-${idx}`}
                                        position={[v.lat, v.lon]}
                                        icon={windArrowIcon(v.direction)}
                                    >
                                        <Tooltip direction="top" offset={[0, -6]}>
                                            {v.direction}° {v.speed != null ? `· ${v.speed} mph` : ""}
                                        </Tooltip>
                                    </Marker>
                                ))}

                                {/* County Marker */}
                                {showCountyValues && hasRenderableLayer && currentData?.county_values?.map((c, idx) => {
                                    const label = activeTab === "precip1" || activeTab === "precip2"
                                        ? c.value.toFixed(1)
                                        : Math.round(c.value).toString();
                                    return (
                                        <Marker
                                            key={`cv-${c.name}-${idx}`}
                                            position={[c.lat, c.lon]}
                                            icon={countyValueIcon(label)}
                                        >
                                            <Tooltip direction="top" offset={[0, -10]}>
                                                {c.name} County: {c.value}{currentData.unit}
                                            </Tooltip>
                                        </Marker>
                                    );
                                })}
                            </MapContainer>

                            {/* Rendered as a sibling of the map, not a child of MapContainer —
                                Leaflet only expects its own layer components inside MapContainer,
                                so a plain <div> in there is invalid even when it doesn't crash. */}
                            {!loadingData && !hasRenderableLayer && (
                                <div className="env-no-data-note">
                                    No data available for this hour yet
                                    {activeTab === "precip1" || activeTab === "precip2"
                                        ? " — MRMS precipitation can lag the live feed by about an hour."
                                        : "."}
                                    {" "}Try a different hour on the slider.
                                </div>
                            )}

                            {hasRenderableLayer && (() => {
                                const bins = currentData!.legend_bins ?? [];
                                const boundaries = bins.length
                                    ? [bins[0].from, ...bins.map(b => b.to)].reverse()
                                    : [];
                                // top-based %, ascending as value descends (highest first)
                                const truePct = boundaries.map((_, idx) => {
                                    return (idx / (boundaries.length - 1)) * 100;
                                });
                                const labelPx = declutterTopPositions(truePct);

                                return (
                                    <div className="env-legend">
                                        <div className="env-legend-unit">{currentData!.unit}</div>
                                        <div className="env-legend-row">
                                            <div className="env-legend-bar">
                                                {[...bins].reverse().map((bin, idx) => {
                                                    const binHeight = 100 / bins.length;

                                                    return (
                                                        <div
                                                            key={idx}
                                                            className="env-legend-bin"
                                                            style={{
                                                                top: `${idx * binHeight}%`,
                                                                height: `${binHeight}%`,
                                                                backgroundColor: bin.color,
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </div>
                                            <div className="env-legend-labels">
                                                {boundaries.map((v, idx) => {
                                                    const truePx = (truePct[idx] / 100) * LEGEND_BAR_HEIGHT;
                                                    const labelYPx = labelPx[idx];
                                                    const needsLeader = Math.abs(labelYPx - truePx) > 2;
                                                    return (
                                                        <React.Fragment key={idx}>
                                                            {needsLeader && (
                                                                <span
                                                                    className="env-legend-leader"
                                                                    style={{ top: `${truePx}px` }}
                                                                />
                                                            )}
                                                            <span
                                                                className="env-legend-label"
                                                                style={{ top: `${labelYPx}px` }}
                                                            >
                                                                {activeTab === "precip1" || activeTab === "precip2"
                                                                    ? v
                                                                    : Math.round(v)}
                                                            </span>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="env-about">
                            <h2 className="env-about-title">{activeConfig.aboutTitle}</h2>
                            <p className="env-about-desc">{activeConfig.aboutDescription}</p>
                        </div>
                    </div>
                ) : (
                    <div className="env-card-grid">
                        {VARIABLES.map(variable => (
                            <button
                                key={variable.key}
                                type="button"
                                className="env-card"
                                onClick={() => handleSelectVariable(variable.key)}
                            >
                                <div className="env-card-image-wrapper">
                                    <img src={variable.image} alt={variable.label} className="env-card-image" />
                                </div>
                                <div className="env-card-body">
                                    <h3 className="env-card-title">{variable.label}</h3>
                                    <p className="env-card-desc">{variable.shortDescription}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Environmental;